// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {useListNavigation} from '@app/features/app/hooks/useListNavigation';
import {isIMEComposing} from '@app/features/messaging/utils/IMECompositionUtils';
import * as QuickSwitcherCommands from '@app/features/search/commands/QuickSwitcherCommands';
import {QuickSwitcherBottomSheet} from '@app/features/search/components/bottomsheets/QuickSwitcherBottomSheet';
import quickStyles from '@app/features/search/components/quick_switcher/QuickSwitcherModal.module.css';
import QuickSwitcher from '@app/features/search/state/QuickSwitcher';
import type {QuickSwitcherExecutableResult, QuickSwitcherResult} from '@app/features/search/state/QuickSwitcherTypes';
import {
	createSections,
	getQuickSwitcherResultAccessibilityMetadata,
	getResultKey,
	getViewContext,
	handleContextMenu,
	PREFIX_HINTS,
	type QuickSwitcherSection,
	renderIcon,
	useQuickSwitcherInputFocus,
	useQuickSwitcherKeyboardHandling,
} from '@app/features/search/utils/QuickSwitcherModalUtils';
import {Input} from '@app/features/ui/components/form/Input';
import {MentionBadge} from '@app/features/ui/components/MentionBadge';
import {Scroller, type ScrollerHandle} from '@app/features/ui/components/Scroller';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import LayerManager from '@app/features/ui/state/LayerManager';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {QuickSwitcherResultTypes} from '@app/vendor/constants/QuickSwitcherConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useId, useMemo, useRef} from 'react';

const getQuickSwitcherOptionId = (baseId: string, index: number): string => `${baseId}-option-${index}`;
const ResultRow = observer(
	({
		result,
		index,
		isKeyboardSelected,
		isHovered,
		onHover,
		onMouseLeave,
		onConfirm,
		optionId,
		positionInSet,
		setSize,
		innerRef,
	}: {
		result: QuickSwitcherResult;
		index: number;
		isKeyboardSelected: boolean;
		isHovered: boolean;
		onHover: (index: number) => void;
		onMouseLeave: () => void;
		onConfirm: (result: QuickSwitcherExecutableResult) => void;
		optionId: string;
		positionInSet: number;
		setSize: number;
		innerRef?: React.Ref<HTMLDivElement>;
	}) => {
		const {i18n} = useLingui();
		if (result.type === QuickSwitcherResultTypes.HEADER) {
			return (
				<div key={getResultKey(result)} className={quickStyles.sectionHeader}>
					{result.title}
				</div>
			);
		}
		const executableResult = result as QuickSwitcherExecutableResult;
		const resultMetadata = getQuickSwitcherResultAccessibilityMetadata(executableResult, i18n);
		const {guildName, isChannel, isUser, label: optionLabel, mentionCount, subtext, unreadCount} = resultMetadata;
		const hasUnread = unreadCount > 0 || mentionCount > 0;
		const isActive = isKeyboardSelected || isHovered;
		const isHighlight =
			hasUnread &&
			(executableResult.type === QuickSwitcherResultTypes.TEXT_CHANNEL ||
				executableResult.type === QuickSwitcherResultTypes.VOICE_CHANNEL ||
				executableResult.type === QuickSwitcherResultTypes.GROUP_DM);
		const handleMouseEnter = () => onHover(index);
		const handleClick = (event: React.MouseEvent) => {
			event.preventDefault();
			onConfirm(executableResult);
		};
		const handleOptionKeyDown = (event: React.KeyboardEvent) => {
			if (event.key !== 'Enter' && event.key !== ' ') {
				return;
			}
			event.preventDefault();
			onConfirm(executableResult);
		};
		const iconRendered = renderIcon(
			executableResult,
			isHighlight,
			quickStyles.optionIcon,
			quickStyles.optionIconHighlight,
		);
		const key = getViewContext(executableResult)
			? `${executableResult.type}-${getViewContext(executableResult)}-${executableResult.id}`
			: `${executableResult.type}-${executableResult.id}`;
		return (
			<FocusRing offset={-2}>
				<div
					id={optionId}
					role="option"
					aria-label={optionLabel}
					aria-selected={isKeyboardSelected}
					aria-posinset={positionInSet}
					aria-setsize={setSize}
					className={clsx(quickStyles.option, isActive && quickStyles.optionActive)}
					ref={innerRef}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={onMouseLeave}
					onMouseDown={(event) => event.preventDefault()}
					onClick={handleClick}
					onKeyDown={handleOptionKeyDown}
					onContextMenu={(event) => handleContextMenu(event, executableResult)}
					key={key}
					tabIndex={-1}
				>
					<div className={quickStyles.optionContent}>
						{iconRendered.type === 'avatar' ? (
							<div className={quickStyles.avatar}>{iconRendered.content}</div>
						) : iconRendered.type === 'guild' ? (
							<div className={quickStyles.guildIcon}>{iconRendered.content}</div>
						) : (
							iconRendered.content
						)}
						<div className={clsx(quickStyles.optionText, isHighlight && quickStyles.optionHighlight)}>
							<div className={quickStyles.optionPrimary}>
								<div className={quickStyles.optionTitle}>{executableResult.title}</div>
								{mentionCount > 0 && (
									<span className={quickStyles.optionMention}>
										<MentionBadge mentionCount={mentionCount} size="small" />
									</span>
								)}
								{subtext && (
									<div
										className={clsx(
											quickStyles.optionDescription,
											isChannel && quickStyles.optionCategory,
											isUser && quickStyles.optionUserTag,
										)}
									>
										{subtext}
									</div>
								)}
							</div>
							{guildName && <div className={quickStyles.optionMeta}>{guildName}</div>}
						</div>
					</div>
				</div>
			</FocusRing>
		);
	},
);
const QuickSwitcherModalComponent: React.FC = observer(() => {
	const {t, i18n} = useLingui();
	const {isOpen, query, results, selectedIndex} = QuickSwitcher;
	const quickSwitcherId = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const scrollerRef = useRef<ScrollerHandle>(null);
	const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
	const shouldScrollToSelection = useRef(false);
	const isMobile = MobileLayout.isMobileLayout();
	const inputId = `${quickSwitcherId}-input`;
	const listboxId = `${quickSwitcherId}-results`;
	const statusId = `${quickSwitcherId}-status`;
	const suggestionStatusId = `${quickSwitcherId}-suggestion-status`;
	const hintId = `${quickSwitcherId}-hint`;
	const {
		keyboardFocusIndex,
		hoverIndexForRender,
		handleMouseEnter: handleHoverIndex,
		handleMouseLeave,
		setSelectedIndex: setKeyboardIndex,
	} = useListNavigation({
		itemCount: results.length,
		initialIndex: selectedIndex >= 0 ? selectedIndex : 0,
		loop: true,
	});
	if (rowRefs.current.length !== results.length) {
		rowRefs.current = Array(results.length).fill(null);
	}
	useEffect(() => {
		if (results.length === 0) {
			setKeyboardIndex(-1);
			handleMouseLeave();
			return;
		}
		const clamped = selectedIndex >= 0 ? Math.min(selectedIndex, results.length - 1) : 0;
		setKeyboardIndex(clamped);
	}, [handleMouseLeave, results.length, selectedIndex, setKeyboardIndex]);
	useEffect(() => {
		handleMouseLeave();
	}, [handleMouseLeave, results.length]);
	useQuickSwitcherKeyboardHandling(isOpen, isMobile, inputRef, query);
	useQuickSwitcherInputFocus(isOpen, isMobile, undefined, inputRef);
	useEffect(() => {
		if (!isOpen || isMobile) {
			return;
		}
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
				return;
			}
			event.preventDefault();
			shouldScrollToSelection.current = true;
			QuickSwitcherCommands.moveSelection(event.key === 'ArrowDown' ? 'down' : 'up');
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isMobile, isOpen]);
	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		QuickSwitcherCommands.search(event.target.value);
	}, []);
	const handleKeyDown = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (isIMEComposing(event)) {
			return;
		}
		switch (event.key) {
			case 'ArrowDown':
			case 'ArrowUp':
				event.preventDefault();
				event.stopPropagation();
				shouldScrollToSelection.current = true;
				QuickSwitcherCommands.moveSelection(event.key === 'ArrowDown' ? 'down' : 'up');
				break;
			case 'Enter':
				event.preventDefault();
				await QuickSwitcherCommands.confirmSelection();
				break;
			case 'Escape':
				event.preventDefault();
				QuickSwitcherCommands.hide();
				break;
			default:
				break;
		}
	}, []);
	const handleClose = useCallback(() => {
		if (LayerManager.hasType('contextmenu')) {
			return;
		}
		QuickSwitcherCommands.hide();
	}, []);
	const handleHover = useCallback(
		(index: number) => {
			shouldScrollToSelection.current = false;
			handleHoverIndex(index);
		},
		[handleHoverIndex],
	);
	const handleConfirm = useCallback((result: QuickSwitcherExecutableResult) => {
		void QuickSwitcherCommands.switchTo(result);
	}, []);
	useEffect(() => {
		if (!shouldScrollToSelection.current || keyboardFocusIndex < 0) {
			shouldScrollToSelection.current = false;
			return;
		}
		shouldScrollToSelection.current = false;
		const node = rowRefs.current[keyboardFocusIndex];
		if (node) {
			scrollerRef.current?.scrollIntoViewNode({node: node as HTMLElement, padding: 32});
		}
	}, [keyboardFocusIndex]);
	const sections = useMemo(() => createSections(results), [results]);
	const selectableIndices = useMemo(
		() =>
			results.reduce<Array<number>>((indices, result, index) => {
				if (result.type !== QuickSwitcherResultTypes.HEADER) {
					indices.push(index);
				}
				return indices;
			}, []),
		[results],
	);
	const selectedOptionPosition = keyboardFocusIndex >= 0 ? selectableIndices.indexOf(keyboardFocusIndex) + 1 : 0;
	const resultCount = useMemo(
		() => results.filter((result) => result.type !== QuickSwitcherResultTypes.HEADER).length,
		[results],
	);
	const activeDescendant =
		keyboardFocusIndex >= 0 && results[keyboardFocusIndex]?.type !== QuickSwitcherResultTypes.HEADER
			? getQuickSwitcherOptionId(listboxId, keyboardFocusIndex)
			: undefined;
	const activeSuggestionStatus = useMemo(() => {
		const result = results[keyboardFocusIndex];
		if (!result || result.type === QuickSwitcherResultTypes.HEADER) {
			return resultCount === 0 ? t`No autocomplete suggestion` : '';
		}
		const label = getQuickSwitcherResultAccessibilityMetadata(result as QuickSwitcherExecutableResult, i18n).label;
		const trimmedQuery = query.trim();
		if (trimmedQuery.length > 0) {
			return selectedOptionPosition > 0
				? t`Autocomplete suggestion for ${trimmedQuery}: ${label}, ${selectedOptionPosition} of ${resultCount}`
				: t`Autocomplete suggestion for ${trimmedQuery}: ${label}`;
		}
		return selectedOptionPosition > 0
			? t`Autocomplete suggestion: ${label}, ${selectedOptionPosition} of ${resultCount}`
			: t`Autocomplete suggestion: ${label}`;
	}, [i18n, keyboardFocusIndex, query, resultCount, results, selectedOptionPosition, t]);
	const resultStatus = QuickSwitcher.isLoadingMemberResults
		? t`Searching people...`
		: resultCount === 0
			? t`No matches found`
			: resultCount === 1
				? t`1 result available`
				: t`${resultCount} results available`;
	if (!isOpen) {
		return null;
	}
	if (isMobile) {
		return <QuickSwitcherBottomSheet isOpen={isOpen} onClose={QuickSwitcherCommands.hide} />;
	}
	return (
		<Modal.Root
			centered
			className={quickStyles.modalRoot}
			onClose={handleClose}
			initialFocusRef={inputRef}
			transitionPreset="instant"
		>
			<Modal.ScreenReaderLabel text={t`Quick Switcher`} />
			<div className={quickStyles.container}>
				<div className={quickStyles.header}>
					<Input
						id={inputId}
						ref={inputRef}
						value={query}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						placeholder={t`Search for channels, people, or communities`}
						spellCheck={false}
						className={quickStyles.inputBackground}
						role="combobox"
						aria-autocomplete="list"
						aria-haspopup="listbox"
						aria-controls={resultCount > 0 ? listboxId : undefined}
						aria-describedby={`${statusId} ${suggestionStatusId} ${hintId}`}
						aria-expanded={resultCount > 0}
						aria-label={t`Quick Switcher search`}
						aria-activedescendant={activeDescendant}
					/>
					<div id={hintId} className={quickStyles.srOnly}>
						{t`Use Up and Down arrows to choose a result, Enter to open it, and Escape to close.`}
					</div>
				</div>
				<div className={quickStyles.list}>
					<div id={statusId} className={quickStyles.srOnly} role="status" aria-live="polite" aria-atomic="true">
						{resultStatus}
					</div>
					<div
						id={suggestionStatusId}
						className={quickStyles.srOnly}
						role="status"
						aria-live="polite"
						aria-atomic="true"
					>
						{activeSuggestionStatus}
					</div>
					<Scroller
						ref={scrollerRef}
						className={quickStyles.scrollerContainer}
						overflow="scroll"
						key="quick_switcher-desktop-scroller"
					>
						{results.length === 0 ? (
							<div className={quickStyles.emptyState} role="status" aria-live="polite">
								<div className={quickStyles.emptyStateTitle}>{t`No matches found`}</div>
								<div className={quickStyles.emptyStateHint}>
									{t`Try a different name or use @ / # / ! / * prefixes to filter results.`}
								</div>
							</div>
						) : (
							<div id={listboxId} role="listbox" aria-label={t`Quick Switcher results`}>
								{sections.map((section: QuickSwitcherSection, sidx: number) => {
									const sectionHeaderId = section.header ? `${listboxId}-section-${sidx}` : undefined;
									const sectionAriaProps = sectionHeaderId
										? ({role: 'group', 'aria-labelledby': sectionHeaderId} as const)
										: ({role: 'presentation'} as const);
									return (
										<div key={`section-${sidx}`} className={quickStyles.section} {...sectionAriaProps}>
											{section.header && (
												<div id={sectionHeaderId} className={quickStyles.sectionHeader}>
													{section.header.title}
												</div>
											)}
											{section.rows.map(({result, index}: {result: QuickSwitcherExecutableResult; index: number}) => (
												<ResultRow
													key={getResultKey(result)}
													result={result}
													index={index}
													isKeyboardSelected={index === keyboardFocusIndex}
													isHovered={index === hoverIndexForRender}
													onHover={handleHover}
													onMouseLeave={handleMouseLeave}
													onConfirm={handleConfirm}
													optionId={getQuickSwitcherOptionId(listboxId, index)}
													positionInSet={selectableIndices.indexOf(index) + 1}
													setSize={resultCount}
													innerRef={(node) => {
														rowRefs.current[index] = node;
													}}
												/>
											))}
										</div>
									);
								})}
							</div>
						)}
					</Scroller>
				</div>
				<div className={quickStyles.footer}>
					<Trans>
						Start searches with{' '}
						<Tooltip text={i18n._(PREFIX_HINTS[0].label)} position="top">
							<span className={quickStyles.footerCode}>@</span>
						</Tooltip>{' '}
						<Tooltip text={i18n._(PREFIX_HINTS[1].label)} position="top">
							<span className={quickStyles.footerCode}>#</span>
						</Tooltip>{' '}
						<Tooltip text={i18n._(PREFIX_HINTS[2].label)} position="top">
							<span className={quickStyles.footerCode}>!</span>
						</Tooltip>{' '}
						<Tooltip text={i18n._(PREFIX_HINTS[3].label)} position="top">
							<span className={quickStyles.footerCode}>*</span>
						</Tooltip>{' '}
						<Tooltip text={i18n._(PREFIX_HINTS[4].label)} position="top">
							<span className={quickStyles.footerCode}>&gt;</span>
						</Tooltip>{' '}
						to narrow down results.
					</Trans>
				</div>
			</div>
		</Modal.Root>
	);
});
export const QuickSwitcherModal = Object.assign(QuickSwitcherModalComponent, {
	disableBackdropOnMobile: true,
});
